<script setup>
import { computed } from 'vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  getFileName,
  showActionInput,
} from 'dashboard/helper/automationHelper';

const props = defineProps({
  actionTypes: {
    type: Array,
    required: true,
  },
  // Read by the scheduled-message action, whose own fields depend on what the rule is
  // conditioned on.
  conditions: {
    type: Array,
    default: () => [],
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  files: {
    type: Array,
    default: () => [],
  },
  showFileName: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
  appendNewAction: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  showActionBoardWarning: {
    type: Function,
    default: () => false,
  },
  showActionOrderWarning: {
    type: Function,
    default: () => false,
  },
  isKanbanEvent: {
    type: Boolean,
    default: false,
  },
  selectedBoardId: {
    type: [Number, String],
    default: null,
  },
});

const actions = defineModel({ type: Array, required: true });

const hasActionErrors = computed(() =>
  Object.keys(props.errors).some(key => key.startsWith('action_'))
);
</script>

<template>
  <section>
    <label>
      {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
    </label>
    <ul
      class="grid p-3 list-none border-solid outline outline-1 rounded-xl -outline-offset-1"
      :class="
        hasActionErrors
          ? 'outline-n-ruby-5 bg-n-ruby-2/50'
          : 'outline-n-weak dark:outline-n-strong'
      "
    >
      <AutomationActionInput
        v-for="(action, i) in actions"
        :key="`${i}-${selectedBoardId}`"
        v-model="actions[i]"
        :action-types="actionTypes"
        :conditions="conditions"
        dropdown-max-height="max-h-72"
        :dropdown-values="
          getActionDropdownValues(action.action_name, conditions, actions)
        "
        :show-action-input="showActionInput(actionTypes, action.action_name)"
        :show-select-board-warning="showActionBoardWarning(action)"
        :show-order-warning="showActionOrderWarning(i)"
        :is-kanban-event="isKanbanEvent"
        :selected-board-id="selectedBoardId"
        :error-message="
          errors[`action_${i}`]
            ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
            : ''
        "
        :initial-file-name="showFileName ? getFileName(action, files) : ''"
        @reset-action="resetAction(i)"
        @remove-action="removeAction(i)"
      />
      <div class="pt-2">
        <NextButton
          icon="i-lucide-plus"
          blue
          faded
          sm
          :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
          @click="appendNewAction"
        />
      </div>
    </ul>
  </section>
</template>
